import { Outlet, RouteObject, createBrowserRouter } from "react-router-dom"
import { lazy, Suspense } from "react"
import {
  RequireSystemAdminSignedIn,
  RequireUserSignedIn,
  RequireUserSignedOut,
} from "~/auth/auth-layouts"
import { LoginLayout } from "~/auth/login-layout"
import {
  adminAdminUsersPath,
  adminAuraProductsPath,
  adminAuraCashOutPath,
  adminAuraCashOutHistoryPath,
  adminDashboardPath,
  adminCreateDemoRoomPath,
  adminEditRoomPath,
  adminEditUserPath,
  adminLoginPath,
  adminMessageModerationPath,
  adminUserModerationPath,
  adminNewAdminUserPath,
  adminRoomPath,
  adminRoomVipsPath,
  adminRoomsPath,
  adminSubscriptionProductsPath,
  adminUserDetailPath,
  adminUsersPath,
  adminMessageDetailPath,
  comingSoonPath,
  downloadPath,
  emailAuthPath,
  invitePath,
  landingPath,
  privacyPath,
  profilePath,
  rootPath,
  shareMessageCommentPath,
  shareMessageCardPath,
  shareMessagePath,
  shareRoomCardPath,
  shareRoomPath,
  supportPath,
  termsPath,
  adminRoomSubscriptionsPath,
  adminRoomHostStatsPath,
  adminCacheStatsPath,
  adminLoadSheddingStatsPath,
  adminNewMemberStatsPath,
  adminPredictionsPath,
  adminEarningsPlansPath,
  adminEarningsPlanPath,
  adminEditEarningsPlanPath,
  adminCreateEarningsPlanPath,
  adminCreatePredictionGroupPath,
  adminUpdatePredictionGroupPath,
  adminInputPredictionResultsPath,
  sharePostCommentPath,
  sharePostPath,
  adminRoomEventsPath,
  adminCreateRoomEventPath,
  adminEditRoomEventPath,
  adminRoomScheduledMessagesPath,
} from "~/common/paths"
import { ComingSoonScreen } from "~/landing/coming-soon-screen"
import { LandingScreen } from "~/landing/landing-screen"
import { PrivacyScreen } from "~/landing/privacy-screen"
import { SupportScreen } from "~/landing/support-screen"
import { TermsScreen } from "~/landing/terms-screen"
import { SidebarLayout } from "~/layouts/sidebar-layout"
import { EmailAuthScreen } from "~/login/email-auth-screen"
import { EmailLoginScreen } from "~/login/email-login-screen"
import { ProfileScreen } from "~/profile/profile-screen"
import { InviteScreen } from "~/share/invite-screen"
import { ShareMessageCardScreen } from "~/share/message-card-screen"
import { ShareMessageScreen } from "~/share/message-screen"
import { ShareRoomCardScreen } from "~/share/room-card-screen"
import { ShareRoomScreen } from "~/share/room-screen"
import { DownloadScreen } from "../landing/download-screen"
import { ErrorBoundary } from "../ui/error-boundary"
import { RootLayout } from "./root-layout"

// Lazy load admin components
const AdminAdminUsersScreen = lazy(() =>
  import("~/admin/admin-admin-users-screen").then((m) => ({ default: m.AdminAdminUsersScreen }))
)
const AdminDashboardScreen = lazy(() =>
  import("~/admin/admin-dashboard-screen").then((m) => ({ default: m.AdminDashboardScreen }))
)
const AdminEditRoomScreen = lazy(() =>
  import("~/admin/admin-edit-room-screen/admin-edit-room-screen").then((m) => ({
    default: m.AdminEditRoomScreen,
  }))
)
const AdminEditUserScreen = lazy(() =>
  import("~/admin/admin-edit-user-screen/admin-edit-user-screen").then((m) => ({
    default: m.AdminEditUserScreen,
  }))
)
const AdminNewUserAdminScreen = lazy(() =>
  import("~/admin/admin-new-admin-user-screen").then((m) => ({
    default: m.AdminNewUserAdminScreen,
  }))
)
const AdminRoomScreen = lazy(() =>
  import("~/admin/admin-room-screen/admin-room-screen").then((m) => ({
    default: m.AdminRoomScreen,
  }))
)
const AdminRoomVipsScreen = lazy(() =>
  import("~/admin/admin-room-vips-screen").then((m) => ({ default: m.AdminRoomVipsScreen }))
)
const AdminCreateDemoRoomScreen = lazy(() =>
  import("~/admin/admin-create-demo-room-screen/admin-create-demo-room-screen").then((m) => ({
    default: m.AdminCreateDemoRoomScreen,
  }))
)
const AdminRoomsScreen = lazy(() =>
  import("~/admin/admin-rooms-screen").then((m) => ({ default: m.AdminRoomsScreen }))
)
const AdminUserDetailScreen = lazy(() =>
  import("~/admin/admin-user-detail-screen/admin-user-detail-screen").then((m) => ({
    default: m.AdminUserDetailScreen,
  }))
)
const AdminUsersScreen = lazy(() =>
  import("~/admin/admin-users-screen").then((m) => ({ default: m.AdminUsersScreen }))
)
const AdminModerationScreen = lazy(() =>
  import("~/admin/admin-moderation-screen/admin-moderation-screen").then((m) => ({
    default: m.AdminModerationScreen,
  }))
)
const AdminRoomSubscriptionsScreen = lazy(() =>
  import("~/admin/admin-room-subscriptions-screen").then((m) => ({
    default: m.AdminRoomSubscriptionsScreen,
  }))
)
const AdminRoomHostStatsScreen = lazy(() =>
  import("~/admin/admin-room-host-stats-screen").then((m) => ({
    default: m.AdminRoomHostStatsScreen,
  }))
)
const AdminCacheStatsScreen = lazy(() =>
  import("~/admin/admin-cache-stats-screen").then((m) => ({
    default: m.AdminCacheStatsScreen,
  }))
)
const AdminLoadSheddingStatsScreen = lazy(() =>
  import("~/admin/admin-load-shedding-stats-screen").then((m) => ({
    default: m.AdminLoadSheddingStatsScreen,
  }))
)
const AdminNewMemberStatsScreen = lazy(() =>
  import("~/admin/admin-new-member-stats-screen").then((m) => ({
    default: m.AdminNewMemberStatsScreen,
  }))
)
const AdminSubscriptionProductsScreen = lazy(() =>
  import("~/admin/admin-room-subscription-products-screen/admin-subscription-products-screen").then(
    (m) => ({ default: m.AdminSubscriptionProductsScreen })
  )
)
const AdminMessageDetailScreen = lazy(() =>
  import("~/admin/admin-message-detail-screen").then((m) => ({
    default: m.AdminMessageDetailScreen,
  }))
)
const AdminPredictionsScreen = lazy(() =>
  import("~/admin/predictions/admin-predictions-screen").then((m) => ({
    default: m.AdminPredictionsScreen,
  }))
)
const AdminEarningsPlansScreen = lazy(() =>
  import("~/admin/earnings/admin-earnings-plans-screen").then((m) => ({
    default: m.AdminEarningsPlansScreen,
  }))
)
const AdminCreateEarningsPlanScreen = lazy(() =>
  import("~/admin/earnings/admin-create-earnings-plan-screen").then((m) => ({
    default: m.AdminCreateEarningsPlanScreen,
  }))
)
const AdminEarningsPlanScreen = lazy(() =>
  import("~/admin/earnings/admin-earnings-plan-screen").then((m) => ({
    default: m.AdminEarningsPlanScreen,
  }))
)
const AdminUpdateEarningsPlanScreen = lazy(() =>
  import("~/admin/earnings/admin-update-earnings-plan-screen").then((m) => ({
    default: m.AdminUpdateEarningsPlanScreen,
  }))
)
const AdminCreatePredictionGroupScreen = lazy(() =>
  import("~/admin/predictions/admin-create-prediction-group-screen").then((m) => ({
    default: m.AdminCreatePredictionGroupScreen,
  }))
)
const AdminUpdatePredictionGroupScreen = lazy(() =>
  import("~/admin/predictions/admin-update-prediction-group-screen").then((m) => ({
    default: m.AdminUpdatePredictionGroupScreen,
  }))
)
const AdminInputResultsScreen = lazy(() =>
  import("~/admin/predictions/admin-input-results-screen").then((m) => ({
    default: m.AdminInputResultsScreen,
  }))
)
const AdminAuraScreen = lazy(() =>
  import("~/admin/admin-aura-screen/admin-aura-screen").then((m) => ({
    default: m.AdminAuraScreen,
  }))
)
const AdminAuraCashOutHistoryScreen = lazy(() =>
  import("~/admin/admin-aura-screen/admin-aura-cash-out-history-screen").then((m) => ({
    default: m.AdminAuraCashOutHistoryScreen,
  }))
)
const AdminRoomEventsScreen = lazy(() =>
  import("~/admin/admin-room-events-screen/admin-room-events-screen").then((m) => ({
    default: m.AdminRoomEventsScreen,
  }))
)
const AdminRoomScheduledMessagesScreen = lazy(() =>
  import("~/admin/admin-room-scheduled-messages-screen/admin-room-scheduled-messages-screen").then(
    (m) => ({ default: m.AdminRoomScheduledMessagesScreen })
  )
)
const AdminEventFormScreen = lazy(() =>
  import("~/admin/admin-event-form-screen/admin-event-form-screen").then((m) => ({
    default: m.AdminEventFormScreen,
  }))
)

// Loading component for Suspense fallbacks
const LoadingComponent = () => (
  <div className="flex items-center justify-center p-8">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
  </div>
)

const systemAdminAuthenticatedRoutes: Array<RouteObject> = [
  {
    path: adminDashboardPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminDashboardScreen />
      </Suspense>
    ),
  },
  {
    path: adminAdminUsersPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAdminUsersScreen />
      </Suspense>
    ),
  },
  {
    path: adminMessageModerationPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminModerationScreen />
      </Suspense>
    ),
  },
  {
    path: adminUserModerationPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminModerationScreen />
      </Suspense>
    ),
  },
  {
    path: adminNewAdminUserPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminNewUserAdminScreen />
      </Suspense>
    ),
  },
  {
    path: adminUsersPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminUsersScreen />
      </Suspense>
    ),
  },
  {
    path: adminEditUserPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEditUserScreen />
      </Suspense>
    ),
  },
  {
    path: adminUserDetailPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminUserDetailScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomsScreen />
      </Suspense>
    ),
  },
  {
    path: adminCreateDemoRoomPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminCreateDemoRoomScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomVipsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomVipsScreen />
      </Suspense>
    ),
  },
  {
    path: adminSubscriptionProductsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminSubscriptionProductsScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomSubscriptionsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomSubscriptionsScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomHostStatsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomHostStatsScreen />
      </Suspense>
    ),
  },
  {
    path: adminEditRoomPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEditRoomScreen />
      </Suspense>
    ),
  },
  {
    path: adminMessageDetailPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminMessageDetailScreen />
      </Suspense>
    ),
  },
  {
    path: adminCacheStatsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminCacheStatsScreen />
      </Suspense>
    ),
  },
  {
    path: adminLoadSheddingStatsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminLoadSheddingStatsScreen />
      </Suspense>
    ),
  },
  {
    path: adminNewMemberStatsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminNewMemberStatsScreen />
      </Suspense>
    ),
  },
  {
    path: adminPredictionsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminPredictionsScreen />
      </Suspense>
    ),
  },
  {
    path: adminEarningsPlansPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEarningsPlansScreen />
      </Suspense>
    ),
  },
  {
    path: adminEditEarningsPlanPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminUpdateEarningsPlanScreen />
      </Suspense>
    ),
  },
  {
    path: adminEarningsPlanPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEarningsPlanScreen />
      </Suspense>
    ),
  },
  {
    path: adminCreateEarningsPlanPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminCreateEarningsPlanScreen />
      </Suspense>
    ),
  },
  {
    path: adminCreatePredictionGroupPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminCreatePredictionGroupScreen />
      </Suspense>
    ),
  },
  {
    path: adminUpdatePredictionGroupPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminUpdatePredictionGroupScreen />
      </Suspense>
    ),
  },
  {
    path: adminInputPredictionResultsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminInputResultsScreen />
      </Suspense>
    ),
  },
  {
    path: adminAuraProductsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAuraScreen />
      </Suspense>
    ),
  },
  {
    path: adminAuraCashOutPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAuraScreen />
      </Suspense>
    ),
  },
  {
    path: adminAuraCashOutHistoryPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminAuraCashOutHistoryScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomEventsPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomEventsScreen />
      </Suspense>
    ),
  },
  {
    path: adminCreateRoomEventPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEventFormScreen />
      </Suspense>
    ),
  },
  {
    path: adminEditRoomEventPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminEventFormScreen />
      </Suspense>
    ),
  },
  {
    path: adminRoomScheduledMessagesPath.pattern,
    element: (
      <Suspense fallback={<LoadingComponent />}>
        <AdminRoomScheduledMessagesScreen />
      </Suspense>
    ),
  },
]

const authenticatedRoutes: Array<RouteObject> = [
  {
    path: profilePath.pattern,
    element: <ProfileScreen />,
  },
]

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RequireSystemAdminSignedIn />,
        children: [
          {
            element: <SidebarLayout />,
            children: [
              {
                element: <Outlet />,
                children: [...systemAdminAuthenticatedRoutes],
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedIn />,
        children: [
          {
            element: <SidebarLayout />,
            children: [
              {
                element: <Outlet />,
                children: [...authenticatedRoutes],
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedOut />,
        children: [
          {
            element: <LoginLayout />,
            children: [
              {
                path: adminLoginPath.pattern,
                element: <EmailLoginScreen />,
              },
              // {
              //   path: adminCredentialsLoginPath.pattern,
              //   element: <CredentialsLoginScreen />,
              // },
              {
                path: emailAuthPath.pattern,
                element: <EmailAuthScreen />,
              },
            ],
          },
        ],
      },
      {
        path: downloadPath.pattern,
        element: <DownloadScreen />,
      },
      {
        path: landingPath.pattern,
        element: <LandingScreen />,
      },
      {
        path: invitePath.pattern,
        element: <InviteScreen />,
      },
      {
        path: shareRoomPath.pattern,
        element: <ShareRoomScreen />,
      },
      {
        path: shareRoomCardPath.pattern,
        element: <ShareRoomCardScreen />,
      },
      {
        path: shareMessagePath.pattern,
        element: <ShareMessageScreen />,
      },
      {
        path: shareMessageCardPath.pattern,
        element: <ShareMessageCardScreen />,
      },
      {
        path: shareMessageCommentPath.pattern,
        element: <ShareMessageScreen />,
      },
      {
        path: sharePostCommentPath.pattern,
        element: <ShareMessageScreen />,
      },
      {
        path: sharePostPath.pattern,
        element: <ShareMessageScreen />,
      },
      {
        path: comingSoonPath.pattern,
        element: <ComingSoonScreen />,
      },
      {
        path: privacyPath.pattern,
        element: <PrivacyScreen />,
      },
      {
        path: termsPath.pattern,
        element: <TermsScreen />,
      },
      {
        path: supportPath.pattern,
        element: <SupportScreen />,
      },
      {
        path: rootPath.pattern,
        element: <LandingScreen />,
      },
    ],
  },
])
